import axios from "axios";
import { ReactNode, createContext, useState, useRef } from "react";
import { ApiEndpoint } from "./utils";

interface appChildren {
  children: ReactNode;
}
export interface ctxDefinition {
  examType?: number;
  setExamType: (id: number) => void;
  loggedIn: boolean;
  setLoggedIn: (isloggedIn: boolean) => void;
  userData: any;
  setUserData: (data: any) => void;
  examStarted: boolean;
  setExamStarted: (isStarted: boolean) => void;
  examStartTime: number;
  setExamStartTime: (time: number) => void;
  setExamSectionStartTime: (time: number) => void;
  config: any;
  setConfig: (data: any) => void;
  sectionQuestionsCache: Record<string, any>;
  setSectionQuestionsCache: (sectionName: string, questions: any[]) => void;
  fetchSectionQuestions: (token: string, sectionName: string) => Promise<any>;
}

const AppDataContext = createContext<ctxDefinition | null>(null);

const AppCtxProvider = ({ children }: appChildren) => {
  const [examType, setExamType] = useState(0);
  const [loggedIn, setLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [examStarted, setExamStarted] = useState(false);
  const [examStartTime, setExamStartTime] = useState(0);
  const [config, setConfig] = useState(null);
  const [sectionQuestionsCache, setSectionQuestionsCacheState] = useState<
    Record<string, any>
  >({});
  const sectionQuestionsCacheRef = useRef<Record<string, any>>({});

  const setSectionQuestionsCache = (sectionName: string, questions: any[]) => {
    // Update ref immediately for synchronous access
    sectionQuestionsCacheRef.current = {
      ...sectionQuestionsCacheRef.current,
      [sectionName]: questions,
    };
    // Update state for reactive components
    setSectionQuestionsCacheState((prev) => ({
      ...prev,
      [sectionName]: questions,
    }));
  };

  const fetchSectionQuestions = async (token: string, sectionName: string) => {
    // Check if already cached
    if (sectionQuestionsCacheRef.current[sectionName]) {
      console.log("Using cached questions for:", sectionName);
      return sectionQuestionsCacheRef.current[sectionName];
    }

    try {
      console.log("Fetching questions for section:", sectionName);
      const resp = await axios.get(
        ApiEndpoint + "wp-json/firstchoiceprep/v1/getExamSectionQuestions",
        {
          params: {
            token: token,
            sectionId: sectionName,
          },
        }
      );

      if (resp.data && resp.data.success && resp.data.questions) {
        console.log(
          "Successfully fetched",
          resp.data.questions.length,
          "questions for:",
          sectionName
        );
        console.log("Sample question structure:", resp.data.questions[0]);

        // Ensure each question has success: true for compatibility
        const questionsWithSuccess = resp.data.questions.map((q: any) => ({
          ...q,
          success: true,
        }));

        setSectionQuestionsCache(sectionName, questionsWithSuccess);
        return questionsWithSuccess;
      }
      console.error("Invalid response format:", resp.data);
      return null;
    } catch (error) {
      console.error("Error fetching section questions:", error);
      return null;
    }
  };

  const setExamSectionStartTime = async (timestamp: number) => {
    setExamStartTime(timestamp);
    setExamStarted(true);
    const temp: any = Object.assign({}, userData);
    temp.sectionStartTime = timestamp;
    temp.examStarted = true;
    setUserData(temp);

    await axios.post(
      ApiEndpoint + "wp-json/firstchoiceprep/v1/updateUserData",
      {
        token: temp?.token,
        fields: ["currentSectionId", "sectionId", "sectionStartTime"],
        values: [
          temp?.currentSectionId,
          temp?.currentSectionId,
          temp?.sectionStartTime,
        ],
      }
    );
  };

  return (
    <AppDataContext.Provider
      value={{
        examType,
        setExamType,
        loggedIn,
        setLoggedIn,
        userData,
        setUserData,
        examStarted,
        setExamStarted,
        examStartTime,
        setExamStartTime,
        setExamSectionStartTime,
        config,
        setConfig,
        sectionQuestionsCache,
        setSectionQuestionsCache,
        fetchSectionQuestions,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
};
export { AppDataContext, AppCtxProvider };
